import { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { forEach } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { empty, errorResponse, isObject, prepareResponseData } from "../../Utilities/utils";
import { Toast } from "primereact/toast";

// css
import "../students/Students.css";

// api
import parentApi from "../../api/Parents";

// data
import { MaritalStatus } from "../../data/MaritalStatus";
import { NigeriaStatesAndLGA } from "../../data/NigeriaStatesAndLGA";
import { titleData } from "../../data/TitleData";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import SelectField from "../../components/form/SelectField";
import Underline from "../../components/others/Underline";
import FullPageLoader from "../../components/loader/FullPageLoader";
import InputField from "../../components/form/InputField";
import { AuthContext } from "../Root/ProtectedRoute";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import { yesNo } from "../../data/yesNo";

const required = "This field is required!";
const validationSchema = Yup.object().shape({
  first_name: Yup.string().required(required),
  last_name: Yup.string().required(required),
  title: Yup.string().required(required),
  middle_name: Yup.string().optional(),
  address: Yup.string().required(required),
  religion: Yup.string().required(required),
  state_of_residence: Yup.string().required(required),
  lga_of_residence: Yup.string().required(required),
  state_of_origin: Yup.string().required(required),
  lga_of_origin: Yup.string().required(required),
  email: Yup.string().email("Invalid email").optional(),
  phone_number: Yup.string().required(required),
  active: Yup.string().required(required),
  marital_status: Yup.string().optional(),
});

const AddUpdateParents = ({ ...props }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
  const [isLoading, setIsLoading] = useState(false);
  const { parentId } = useParams();
  const toastTR = useRef(null);

  // states and LGAs
  const [lgaOfOrigin, setLgaOfOrigin] = useState([]);
  const [lgasOfResidence, setLgasOfResidence] = useState([]);

  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    religion: "",
    phone_number: "",
    address: "",
    title: "",
    state_of_residence: "",
    lga_of_residence: "",
    state_of_origin: "",
    lga_of_origin: "",
    email: "",
    marital_status: "",
    active: "",
  });

  useEffect(() => {
    if (!empty(parentId)) {
      getParentDetails();
    }
  }, [parentId]);

  useEffect(() => {
    const stateOfOrigin = !empty(user.stateOfOrigin) ? user.stateOfOrigin : "";
    const stateOfResidence = !empty(user.stateOfResidence)
      ? user.stateOfResidence
      : "";
    const stateOfOiginLGAs =
      NigeriaStatesAndLGA.find(
        (stateObj) =>
          !empty(stateObj) &&
          !empty(stateObj.title) &&
          stateObj.title === stateOfOrigin
      )?.lgas || [];
    setLgaOfOrigin(stateOfOiginLGAs);
    const stateOfResidenceLGAs =
      NigeriaStatesAndLGA.find(
        (stateObj) =>
          !empty(stateObj) &&
          !empty(stateObj.title) &&
          stateObj.title === stateOfResidence
      )?.lgas || [];
    setLgasOfResidence(stateOfResidenceLGAs);
  }, []);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  const handleStateChange = (setFieldValue, field) => (e) => {
    const selectedState = e.target.value;
    const lgas =
      NigeriaStatesAndLGA.find(
        (stateObj) =>
          !empty(stateObj) &&
          !empty(stateObj.title) &&
          stateObj.title === selectedState
      )?.lgas || [];
    setFieldValue(field, selectedState);
    if (field === "state_of_origin") {
      setLgaOfOrigin(lgas);
    } else if (field === "state_of_residence") {
      setLgasOfResidence(lgas);
    }
  };

  /**
   * A method to get parent details
   * @returns
   */
  const getParentDetails = async () => {
    setIsLoading(true);
    try {
      const response = await parentApi.getParentDetails(parentId, schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Something went wrong!"
            : "Something went wrong"
        );
      }

      if (
        !empty(response_data) &&
        !empty(response_data.response) &&
        isObject(response_data.response)
      ) {
        const userData = response_data.response;
        setInitialValues({
          first_name: !empty(userData.firstName) ? userData.firstName : "",
          last_name: !empty(userData.lastName) ? userData.lastName : "",
          middle_name: !empty(userData.middleName) ? userData.middleName : "",
          title: !empty(userData.title) ? userData.title : "",
          address: !empty(userData.address) ? userData.address : "",
          state_of_residence: !empty(userData.stateOfResidence)
            ? userData.stateOfResidence
            : "",
          lga_of_residence: !empty(userData.lgaOfResidence)
            ? userData.lgaOfResidence
            : "",
          state_of_origin: !empty(userData.stateOfOrigin)
            ? userData.stateOfOrigin
            : "",
          lga_of_origin: !empty(userData.lgaOfOrigin)
            ? userData.lgaOfOrigin
            : "",
          email: !empty(userData.email) ? userData.email : "",
          phone_number: !empty(userData.phoneNumber)
            ? userData.phoneNumber
            : "",
          religion: !empty(userData.religion) ? userData.religion : "",
          marital_status: !empty(userData.maritalStatus)
            ? userData.maritalStatus
            : "",
          active:
            !empty(userData.active) && userData.active === true ? "Yes" : "No",
        });

        const stateOfOrigin = !empty(userData.stateOfOrigin)
          ? userData.stateOfOrigin
          : "";
        const stateOfResidence = !empty(userData.stateOfResidence)
          ? userData.stateOfResidence
          : "";
        const stateOfOiginLGAs =
          NigeriaStatesAndLGA.find(
            (stateObj) =>
              !empty(stateObj) &&
              !empty(stateObj.title) &&
              stateObj.title === stateOfOrigin
          )?.lgas || [];
        setLgaOfOrigin(stateOfOiginLGAs);
        const stateOfResidenceLGAs =
          NigeriaStatesAndLGA.find(
            (stateObj) =>
              !empty(stateObj) &&
              !empty(stateObj.title) &&
              stateObj.title === stateOfResidence
          )?.lgas || [];
        setLgasOfResidence(stateOfResidenceLGAs);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Add / Update Parent Profile
   * @param {*} values
   * @returns
   */
  const handleSubmit = async (values) => {
    if (!isLoading) setIsLoading(true);
    try {
      if (empty(values)) {
        return responseDialog(
          "error",
          "Error Alert",
          "Something went wrong! Try again later."
        );
      }

      const fields = [
        "first_name",
        "last_name",
        "middle_name",
        "address",
        "phone_number",
        "title",
        "marital_status",
        "state_of_origin",
        "lga_of_origin",
        "state_of_residence",
        "lga_of_residence",
        "religion",
        "email",
        "active",
      ];

      const parentDetails = {};
      if (!empty(values)) {
        forEach(fields, (data) => {
          parentDetails[data] =
            !empty(data) && !empty(values[data]) ? values[data] : "";
        });
      }
      parentDetails.school_id = schoolId;
      let response;
      if (empty(parentId)) {
        response = await parentApi.addParent(parentDetails);
      } else {
        response = await parentApi.updateParent(
          schoolId,
          parentId,
          parentDetails
        );
      }
      const response_data = prepareResponseData(response);
      if (isObject(response_data) && !response_data.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Something went wrong!"
        );
      }

      if (isObject(response_data) && response_data.success) {
        if (!empty(parentId)) {
          return navigate(`/parents`, {
            state: {
              parentUpdated: true,
            },
          });
        } else {
          return navigate(`/parents`, {
            state: {
              parentAdded: true,
            },
          });
        }
      }

      navigate("/parents", {
        state: { parentAdded: false, parentUpdated: false },
      });
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="container flex-center-top">
            <MainHeader
              title={
                !empty(parentId)
                  ? "Update Parent Profile"
                  : "Add Parent Profile"
              }
            />
            <div className="form-container mt-30">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, handleChange, setFieldValue }) => (
                  <Form style={{ width: "100%" }}>
                    <div className="form-section-title">
                      School Information
                      <Underline />
                    </div>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Enter First Name"
                        name="first_name"
                        height={50}
                        labelTitle="First Name"
                        required={true}
                      />
                      <InputField
                        placeholder="Enter Last Name"
                        name="last_name"
                        height={50}
                        labelTitle="Last Name"
                        required={true}
                      />
                    </div>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Enter Middle Name"
                        name="middle_name"
                        height={50}
                        labelTitle="Middle Name"
                      />
                      <InputField
                        placeholder="Enter Religion"
                        name="religion"
                        height={50}
                        labelTitle="Religion"
                        required={true}
                      />
                    </div>
                    <div className="app_input_group">
                      <SelectField
                        labelTitle="State of Origin"
                        placeholder="Select State of Origin"
                        name="state_of_origin"
                        options={NigeriaStatesAndLGA}
                        height={50}
                        required={true}
                        valueKey="title"
                        selectedOption={values.state_of_origin}
                        handleChangeFunc={handleStateChange(
                          setFieldValue,
                          "state_of_origin"
                        )}
                      />
                      <SelectField
                        labelTitle="L.G.A of Origin"
                        placeholder="Select L.G.A of Origin"
                        name="lga_of_origin"
                        options={lgaOfOrigin}
                        height={50}
                        required={true}
                        valueKey="title"
                        selectedOption={values.lga_of_origin}
                        handleChangeFunc={handleChange}
                      />
                    </div>
                    <div className="app_input_group">
                      <SelectField
                        labelTitle="State of Residence"
                        placeholder="Select State of Residence"
                        name="state_of_residence"
                        options={NigeriaStatesAndLGA}
                        height={50}
                        required={true}
                        valueKey="title"
                        selectedOption={values.state_of_residence}
                        handleChangeFunc={handleStateChange(
                          setFieldValue,
                          "state_of_residence"
                        )}
                      />
                      <SelectField
                        labelTitle="L.G.A of Residence"
                        placeholder="Select L.G.A of Residence"
                        name="lga_of_residence"
                        options={lgasOfResidence}
                        height={50}
                        required={true}
                        valueKey="title"
                        selectedOption={values.lga_of_residence}
                        handleChangeFunc={handleChange}
                      />
                    </div>
                    <div className="app_input_group">
                      <SelectField
                        labelTitle="Title"
                        placeholder="Select Title"
                        name="title"
                        required={true}
                        options={titleData}
                        height={50}
                        valueKey="title"
                        selectedOption={values.title}
                        handleChangeFunc={handleChange}
                      />
                      <SelectField
                        labelTitle="Marital Status"
                        placeholder="Select Marital Status"
                        name="marital_status"
                        options={MaritalStatus}
                        height={50}
                        valueKey="title"
                        selectedOption={values.marital_status}
                        handleChangeFunc={handleChange}
                      />
                    </div>
                    <div className="form-section-title mt-40">
                      Contact Information
                      <Underline />
                    </div>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Enter Phone Number"
                        name="phone_number"
                        height={50}
                        required={true}
                        labelTitle="Phone Number"
                      />
                      <InputField
                        placeholder="Enter Email"
                        name="email"
                        height={50}
                        labelTitle="Email"
                      />
                    </div>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Address"
                        name="address"
                        height={50}
                        labelTitle="Address"
                        required={true}
                      />
                    </div>
                    <div className="form-section-title mt-40">
                      Other
                      <Underline />
                    </div>
                    <div className="app_input_group md-w-50pc">
                      <SelectField
                        labelTitle="Activate"
                        placeholder="Select Option"
                        name="active"
                        required={true}
                        options={yesNo}
                        height={50}
                        valueKey="title"
                        selectedOption={values.active}
                        handleChangeFunc={handleChange}
                      />
                    </div>
                    <div className="form-button-container">
                      <ButtonIcon
                        height={45}
                        marginTop={5}
                        color="#ffffff"
                        backgroundColor="#633ccd"
                        width={300}
                        borderColor="#633ccd"
                        buttonText={
                          !empty(parentId) ? "Update Parent" : "Add Parent"
                        }
                        type="submit"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </main>
        {isLoading && <FullPageLoader visible={isLoading} />}
        <Toast ref={toastTR} position="bottom-left" />
      </AppWrapper>
    </>
  );
};

export default AddUpdateParents;
